/**
 * Cross-stitch preview — pixelates an uploaded photo client-side
 * to simulate how the embroidery kit will look. No server calls.
 */
(function () {
  // Internal pixels per stitch on the rendering buffer. The buffer is sized
  // for sharpness (not display) — CSS scales it down to whatever the column
  // is wide. Using a generous internal cell keeps the X texture crisp.
  var INTERNAL_CELL = 8;

  // Stitches across by selected size variant. Higher count = more detail,
  // less chunky look. Tuned to keep faces and small features recognisable.
  var SIZE_STITCHES = {
    '30x40': 260,
    '40x50': 340,
    '50x70': 420,
    'default': 300
  };

  function detectSizeKey(root) {
    // Wizard: an active size button takes precedence. Dimensions are sorted
    // so that landscape/portrait variants of the same canvas share a key.
    var active = document.querySelector('[data-pw-size].is-active');
    if (active) {
      var dims = (active.getAttribute('data-pw-size') || '').toLowerCase().split('x').map(Number);
      if (dims.length === 2 && !isNaN(dims[0]) && !isNaN(dims[1])) {
        dims.sort(function (a, b) { return a - b; });
        var k = dims.join('x');
        if (SIZE_STITCHES[k]) return k;
      }
    }
    var form = root.closest('form') || document;
    var checked = form.querySelectorAll('input[type="radio"]:checked, select');
    for (var i = 0; i < checked.length; i++) {
      var val = (checked[i].value || '').toLowerCase().replace(/\s|×/g, 'x').replace('cm', '');
      if (/30x40/.test(val)) return '30x40';
      if (/40x50/.test(val)) return '40x50';
      if (/50x70/.test(val)) return '50x70';
    }
    return 'default';
  }

  function readFileAsImage(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onerror = reject;
      reader.onload = function (ev) {
        var img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = reject;
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Build a single stitch-texture tile (semi-transparent X with highlight).
   * Used as a fillPattern over the entire image to give a fabric/thread look
   * that adapts naturally to whatever colour is underneath.
   */
  function buildStitchTile(cellSize) {
    var tile = document.createElement('canvas');
    tile.width = cellSize;
    tile.height = cellSize;
    var ctx = tile.getContext('2d');
    var pad = Math.max(0.5, cellSize * 0.14);

    ctx.lineCap = 'round';

    // Dark thread shadow — both diagonals, kept subtle so the photo reads through
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = Math.max(0.8, cellSize * 0.13);
    ctx.beginPath();
    ctx.moveTo(pad, pad);
    ctx.lineTo(cellSize - pad, cellSize - pad);
    ctx.moveTo(cellSize - pad, pad);
    ctx.lineTo(pad, cellSize - pad);
    ctx.stroke();

    // Light highlight along one diagonal — gives the thread a slight sheen
    ctx.strokeStyle = 'rgba(255,255,255,0.22)';
    ctx.lineWidth = Math.max(0.5, cellSize * 0.07);
    ctx.beginPath();
    ctx.moveTo(pad + 0.5, pad - 0.5);
    ctx.lineTo(cellSize - pad + 0.5, cellSize - pad - 0.5);
    ctx.stroke();

    return tile;
  }

  /**
   * 4-neighbour unsharp mask on the downscaled stitch grid.
   * Restores edge contrast lost in the high-quality downscale so faces
   * and outlines read sharper after nearest-neighbour upscale.
   */
  function sharpenInPlace(imgData, amount) {
    var w = imgData.width, h = imgData.height;
    var dst = imgData.data;
    var src = new Uint8ClampedArray(dst);
    var row = w * 4;
    for (var y = 1; y < h - 1; y++) {
      for (var x = 1; x < w - 1; x++) {
        var i = (y * w + x) * 4;
        for (var c = 0; c < 3; c++) {
          var center = src[i + c];
          var avg = (src[i - 4 + c] + src[i + 4 + c] + src[i - row + c] + src[i + row + c]) * 0.25;
          var v = center + amount * (center - avg);
          dst[i + c] = v < 0 ? 0 : v > 255 ? 255 : v;
        }
      }
    }
  }

  function clamp8(v) { return v < 0 ? 0 : v > 255 ? 255 : v; }

  /**
   * Separable 3×3 box blur (RGB only) on the small paint grid. Merges photo
   * speckle into smooth, paintable regions before posterising.
   */
  function boxBlurRGB(imgData) {
    var w = imgData.width, h = imgData.height, src = imgData.data;
    var tmp = new Uint8ClampedArray(src);
    var row = w * 4, x, y, c, i;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        i = (y * w + x) * 4;
        for (c = 0; c < 3; c++) {
          var xm = x > 0 ? src[i - 4 + c] : src[i + c];
          var xp = x < w - 1 ? src[i + 4 + c] : src[i + c];
          tmp[i + c] = (xm + src[i + c] + xp) / 3;
        }
      }
    }
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        i = (y * w + x) * 4;
        for (c = 0; c < 3; c++) {
          var ym = y > 0 ? tmp[i - row + c] : tmp[i + c];
          var yp = y < h - 1 ? tmp[i + row + c] : tmp[i + c];
          src[i + c] = (ym + tmp[i + c] + yp) / 3;
        }
      }
    }
  }

  /** Posterise each channel to `levels` steps → flat, limited-palette regions. */
  function posterizeRGB(imgData, levels) {
    var data = imgData.data, step = 255 / (levels - 1), i;
    for (i = 0; i < data.length; i += 4) {
      data[i]     = Math.round(data[i]     / step) * step;
      data[i + 1] = Math.round(data[i + 1] / step) * step;
      data[i + 2] = Math.round(data[i + 2] / step) * step;
    }
  }

  function _numCmp(p, q) { return p - q; }

  /**
   * 3×3 median filter (RGB). Removes isolated speckle pixels — the noise that
   * otherwise survives posterisation as bright colour dots on flat areas —
   * while keeping edges crisp (unlike a blur, which softens them).
   */
  function median3(imgData) {
    var w = imgData.width, h = imgData.height, s = imgData.data;
    var src = new Uint8ClampedArray(s), row = w * 4;
    var a = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        var xm = x > 0 ? -4 : 0, xp = x < w - 1 ? 4 : 0;
        var ym = y > 0 ? -row : 0, yp = y < h - 1 ? row : 0;
        for (var c = 0; c < 3; c++) {
          a[0] = src[i + ym + xm + c]; a[1] = src[i + ym + c]; a[2] = src[i + ym + xp + c];
          a[3] = src[i + xm + c];      a[4] = src[i + c];      a[5] = src[i + xp + c];
          a[6] = src[i + yp + xm + c]; a[7] = src[i + yp + c]; a[8] = src[i + yp + xp + c];
          a.sort(_numCmp);
          s[i + c] = a[4];
        }
      }
    }
  }

  /** Gentle saturation lift so the reduced palette reads like fresh paint. */
  function saturateRGB(imgData, f) {
    var data = imgData.data, i, r, g, b, gray;
    for (i = 0; i < data.length; i += 4) {
      r = data[i]; g = data[i + 1]; b = data[i + 2];
      gray = 0.299 * r + 0.587 * g + 0.114 * b;
      data[i]     = clamp8(gray + (r - gray) * f);
      data[i + 1] = clamp8(gray + (g - gray) * f);
      data[i + 2] = clamp8(gray + (b - gray) * f);
    }
  }

  /**
   * Median-cut colour quantisation — builds a palette of `numColors` real
   * colours by repeatedly splitting the colour box with the widest channel at
   * its median. This is how paint-by-numbers palettes are made: unlike
   * per-channel posterising it keeps hue coherent, so dark/flat regions map to
   * a couple of consistent paint colours instead of a chaotic patchwork.
   */
  function medianCutPalette(data, numColors) {
    var pixels = [];
    for (var i = 0; i < data.length; i += 4) {
      pixels.push([data[i], data[i + 1], data[i + 2]]);
    }
    var boxes = [pixels];
    while (boxes.length < numColors) {
      var bi = -1, brange = -1, bchan = 0;
      for (var b = 0; b < boxes.length; b++) {
        var box = boxes[b];
        if (box.length < 2) continue;
        var mn0 = 255, mn1 = 255, mn2 = 255, mx0 = 0, mx1 = 0, mx2 = 0;
        for (var p = 0; p < box.length; p++) {
          var px = box[p];
          if (px[0] < mn0) mn0 = px[0]; if (px[0] > mx0) mx0 = px[0];
          if (px[1] < mn1) mn1 = px[1]; if (px[1] > mx1) mx1 = px[1];
          if (px[2] < mn2) mn2 = px[2]; if (px[2] > mx2) mx2 = px[2];
        }
        var r0 = mx0 - mn0, r1 = mx1 - mn1, r2 = mx2 - mn2;
        var rc = (r0 >= r1 && r0 >= r2) ? 0 : (r1 >= r2 ? 1 : 2);
        var rr = rc === 0 ? r0 : rc === 1 ? r1 : r2;
        if (rr > brange) { brange = rr; bi = b; bchan = rc; }
      }
      if (bi < 0) break;
      var target = boxes[bi], ch = bchan;
      target.sort(function (A, B) { return A[ch] - B[ch]; });
      var mid = target.length >> 1;
      boxes.splice(bi, 1, target.slice(0, mid), target.slice(mid));
    }
    var palette = [];
    for (var b2 = 0; b2 < boxes.length; b2++) {
      var box2 = boxes[b2], n = box2.length || 1, rs = 0, gs = 0, bs = 0;
      for (var p2 = 0; p2 < box2.length; p2++) { rs += box2[p2][0]; gs += box2[p2][1]; bs += box2[p2][2]; }
      palette.push([Math.round(rs / n), Math.round(gs / n), Math.round(bs / n)]);
    }
    return palette;
  }

  /** Snap every pixel to its nearest palette colour (flat paint regions). */
  function mapToPalette(data, palette) {
    var n = palette.length;
    for (var i = 0; i < data.length; i += 4) {
      var r = data[i], g = data[i + 1], b = data[i + 2], best = 0, bd = 1e12;
      for (var k = 0; k < n; k++) {
        var pk = palette[k], dr = pk[0] - r, dg = pk[1] - g, db = pk[2] - b;
        var dd = dr * dr + dg * dg + db * db;
        if (dd < bd) { bd = dd; best = k; }
      }
      var pb = palette[best];
      data[i] = pb[0]; data[i + 1] = pb[1]; data[i + 2] = pb[2];
    }
  }

  /**
   * Paint-by-numbers preview (Eigen Foto Kleuren op Nummer). Turns the photo
   * into a painterly, colour-reduced image with flat regions and soft edges —
   * NO stitch grid, NO thread texture. Mirrors the reference end-results.
   */
  function renderPaintPreview(root, image) {
    var canvas = root.querySelector('[data-cs-canvas]');
    var origImg = root.querySelector('[data-cs-original]');
    var placeholders = root.querySelectorAll('[data-cs-placeholder]');
    if (!canvas || !image) return;

    var aspect = image.height / image.width;
    var gridW = 360;
    var gridH = Math.max(20, Math.round(gridW * aspect));

    // Pass 1: high-quality downscale merges fine detail into paintable regions.
    var small = document.createElement('canvas');
    small.width = gridW;
    small.height = gridH;
    var sctx = small.getContext('2d');
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = 'high';
    sctx.drawImage(image, 0, 0, gridW, gridH);

    // Pass 2: 1× median despeckle → build a 24-colour median-cut palette →
    // snap every pixel to it. Coherent flat paint regions, faithful colours,
    // no per-channel posterise chaos in dark areas.
    try {
      var d = sctx.getImageData(0, 0, gridW, gridH);
      median3(d);
      var palette = medianCutPalette(d.data, 24);
      mapToPalette(d.data, palette);
      sctx.putImageData(d, 0, 0);
    } catch (_) {}

    // Pass 3: upscale WITH smoothing → soft, painterly edges (not pixel blocks).
    var w = Math.min(1200, gridW * 3);
    var h = Math.round(w * aspect);
    canvas.width = w;
    canvas.height = h;
    canvas.removeAttribute('style');
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(small, 0, 0, w, h);

    root.style.setProperty('--cs-aspect', aspect.toFixed(4));
    if (origImg) {
      origImg.src = image.src;
      origImg.style.display = 'block';
    }
    canvas.style.display = 'block';
    placeholders.forEach(function (p) { p.style.display = 'none'; });
    root.classList.add('is-ready');
  }

  function renderPreview(root, image) {
    if (root.getAttribute('data-cs-mode') === 'paint') {
      return renderPaintPreview(root, image);
    }
    var canvas = root.querySelector('[data-cs-canvas]');
    var origImg = root.querySelector('[data-cs-original]');
    var placeholders = root.querySelectorAll('[data-cs-placeholder]');
    var status = root.querySelector('[data-cs-status]');
    if (!canvas || !image) return;

    var stitches = SIZE_STITCHES[detectSizeKey(root)];
    var aspect = image.height / image.width;
    var stitchesY = Math.max(20, Math.round(stitches * aspect));

    // Pass 1: downscale photo to stitch grid (natural color averaging)
    var small = document.createElement('canvas');
    small.width = stitches;
    small.height = stitchesY;
    var sctx = small.getContext('2d');
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = 'high';
    sctx.drawImage(image, 0, 0, stitches, stitchesY);

    try {
      var imgData = sctx.getImageData(0, 0, stitches, stitchesY);
      sharpenInPlace(imgData, 1.1);
      sctx.putImageData(imgData, 0, 0);
    } catch (_) {}

    // Pass 2: scale up onto canvas internal buffer with nearest-neighbour.
    // The canvas is sized in INTERNAL pixels — CSS handles display sizing
    // (object-fit: contain) so the full image always fits, no cropping.
    var cellSize = INTERNAL_CELL;
    var w = cellSize * stitches;
    var h = cellSize * stitchesY;
    canvas.width = w;
    canvas.height = h;
    canvas.removeAttribute('style');

    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(small, 0, 0, w, h);

    // Pass 3: stitch texture overlay
    var tile = buildStitchTile(cellSize);
    var pattern = ctx.createPattern(tile, 'repeat');
    if (pattern) {
      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, w, h);
    }

    // Sync container aspect with image so both columns stay the same height
    root.style.setProperty('--cs-aspect', aspect.toFixed(4));

    if (origImg) {
      origImg.src = image.src;
      origImg.style.display = 'block';
    }
    canvas.style.display = 'block';
    placeholders.forEach(function (p) { p.style.display = 'none'; });
    root.classList.add('is-ready');
    if (status) {
      status.textContent =
        'Voorbeeld: ' + stitches + ' × ' + stitchesY +
        ' steken. Werkelijke borduurwerk bevat meer detail en kleurnuance.';
      status.classList.add('cs-status--active');
    }
  }

  function init(root) {
    if (root.dataset.csInit === '1') return;
    root.dataset.csInit = '1';

    var fileInput = root.querySelector('[data-cs-file]');
    var status = root.querySelector('[data-cs-status]');
    if (!fileInput) return;

    var currentImage = null;

    fileInput.addEventListener('change', function (e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) {
        if (status) status.textContent = 'Bestand is groter dan 20 MB. Kies een kleinere foto.';
        return;
      }
      if (status) status.textContent = 'Voorbeeld wordt gemaakt…';
      readFileAsImage(file).then(function (img) {
        currentImage = img;
        requestAnimationFrame(function () { renderPreview(root, currentImage); });
      }).catch(function () {
        if (status) status.textContent = 'Kon de foto niet laden. Probeer een ander bestand.';
      });
    });

    var debounce;
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (!/^options\[/i.test(t.name) && !/^id$/i.test(t.name)) return;
      if (!currentImage) return;
      clearTimeout(debounce);
      debounce = setTimeout(function () { renderPreview(root, currentImage); }, 100);
    });

    // Wizard size selection — re-render with new stitch density.
    document.addEventListener('pw:size-changed', function () {
      if (!currentImage) return;
      clearTimeout(debounce);
      debounce = setTimeout(function () { renderPreview(root, currentImage); }, 100);
    });
  }

  function bootstrap() {
    document.querySelectorAll('[data-cs-preview]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
